import { useMemo, useRef } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import { useQuery as useQueryRQ } from '@tanstack/react-query';

import { useCategories } from '#hooks/useCategories';
import { usePayeesById } from '#hooks/usePayees';
import { usePlan } from '#hooks/usePlan';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { aqlQuery } from '#queries/aqlQuery';

import {
  detectarSeries,
  inicioPorCuenta,
  MESES_HISTORIAL_GAVI,
} from './cobros';
import type { CargoGavi, SerieCobro } from './cobros';
import { consejosDe, consejosVisibles } from './consejos';
import type { Consejo } from './consejos';
import { contratoDe, escribirEstadoGavi, leerEstadoGavi } from './estadoGavi';
import type { EstadoGavi } from './estadoGavi';
import { esConsumo, reconocerTipo } from './tiposRecibo';

/**
 * ¿Ve esta persona a Gavi en Pagos fijos? Es de Altamar: hoy, con el plan
 * fundador de cortesía, todo el que tiene cuenta. Sin cuenta, no.
 */
export function useGaviActivo(): boolean {
  const { plan } = usePlan();
  return plan?.funciones.gavi === true;
}

/** Pref sincronizada `pagos-fijos-gavi` (ver `estadoGavi.ts`). */
export function useEstadoGavi() {
  const [raw, setRaw] = useSyncedPref('pagos-fijos-gavi');
  const estado = useMemo(() => leerEstadoGavi(raw), [raw]);
  // Dos cambios seguidos antes de que llegue la pref: el segundo parte del
  // primero, no del valor viejo.
  const ultimo = useRef(estado);
  ultimo.current = estado;
  const cambiar = (fn: (e: EstadoGavi) => EstadoGavi) => {
    const nuevo = fn(ultimo.current);
    ultimo.current = nuevo;
    setRaw(escribirEstadoGavi(nuevo));
  };
  return { estado, cambiar };
}

/**
 * Salidas con comercio de los últimos 25 meses (sin traspasos entre cuentas
 * del presupuesto), para ver qué se repite.
 */
export function useCargosGavi(activo: boolean): CargoGavi[] | undefined {
  const hoy = monthUtils.currentDay();
  const desde = monthUtils.subMonths(
    monthUtils.monthFromDate(hoy),
    MESES_HISTORIAL_GAVI,
  );
  const { data: comercios } = usePayeesById();
  const { data } = useQueryRQ({
    queryKey: ['pagos-gavi', 'cargos', desde],
    enabled: activo,
    staleTime: 60_000,
    queryFn: async () => {
      const { data: filas } = await aqlQuery(
        q('transactions')
          .filter({
            amount: { $lt: 0 },
            payee: { $ne: null },
            $or: [
              { 'payee.transfer_acct': null },
              { 'payee.transfer_acct.offbudget': true },
            ],
            date: { $transform: '$month', $gte: desde },
          })
          .select(['id', 'date', 'amount', 'payee', 'category', 'account']),
      );
      return filas as {
        id: string;
        date: string;
        amount: number;
        payee: string | null;
        category: string | null;
        account: string | null;
      }[];
    },
  });
  return useMemo(
    () =>
      data?.map(f => ({
        id: f.id,
        fecha: f.date,
        importe: -f.amount,
        payee: f.payee,
        comercio: (f.payee && comercios?.[f.payee]?.name) || null,
        categoria: f.category,
        cuenta: f.account,
      })),
    [data, comercios],
  );
}

export type DatosGavi = {
  activo: boolean;
  /** Hasta tener los cargos. */
  cargando: boolean;
  series: SerieCobro[];
  /** Cobros que se repiten en categorías que aún no son pago fijo. */
  encontrados: SerieCobro[];
  /** Series vivas de cada categoría, la de más dinero primero. */
  porCategoria: Map<string, SerieCobro[]>;
  /** Consejos que se ven, por categoría de pago fijo. */
  consejos: Map<string, Consejo[]>;
  estado: EstadoGavi;
  cambiar: (fn: (e: EstadoGavi) => EstadoGavi) => void;
  cargos: CargoGavi[];
  inicioCuenta: Map<string, string>;
  nombreCategoria: (id: string | null) => string | null;
};

/** Últimos importes parecidos (±15 %): un recibo, no compras sueltas. */
function importeEstable(serie: SerieCobro): boolean {
  const ultimos = serie.cargos.slice(-3).map(c => c.importe);
  return Math.max(...ultimos) <= Math.min(...ultimos) * 1.15;
}

/**
 * Gavi en Pagos fijos (Altamar): series de cobros, lo encontrado fuera de
 * los pagos fijos y los consejos de cada pago fijo. Sin plan, todo vacío y
 * sin consultas.
 */
export function usePagosGavi(fijos: ReadonlySet<string>): DatosGavi {
  const activo = useGaviActivo();
  const cargosLeidos = useCargosGavi(activo);
  const { estado, cambiar } = useEstadoGavi();
  const { data: { list: categorias } = { list: [] } } = useCategories();
  const hoy = monthUtils.currentDay();

  const calculado = useMemo(() => {
    const cargos = cargosLeidos ?? [];
    const categoria = new Map(categorias.map(c => [c.id, c] as const));
    const nombreCategoria = (id: string | null) =>
      (id && categoria.get(id)?.name) || null;
    const series = detectarSeries(cargos, hoy);
    const inicioCuenta = inicioPorCuenta(cargos);
    const porCategoria = new Map<string, SerieCobro[]>();
    for (const s of series) {
      if (!s.categoria) {
        continue;
      }
      const lista = porCategoria.get(s.categoria) ?? [];
      lista.push(s);
      porCategoria.set(s.categoria, lista);
    }
    const descartados = new Set(estado.descartados);
    const encontrados = series.filter(s => {
      const c = s.categoria ? categoria.get(s.categoria) : undefined;
      if (!c || c.is_income || c.hidden || fijos.has(c.id)) {
        return false;
      }
      if (descartados.has(s.clave) || s.cargos.length < 3 || s.cada > 3) {
        return false;
      }
      const tipo = reconocerTipo(s.comercio, c.name);
      return esConsumo(tipo) || importeEstable(s);
    });
    const consejos = new Map<string, Consejo[]>();
    for (const id of fijos) {
      const lista = consejosDe(
        {
          categoria: id,
          nombreCategoria: nombreCategoria(id) ?? '',
          series: porCategoria.get(id) ?? [],
          contrato: contratoDe(
            estado,
            id,
            (porCategoria.get(id) ?? []).map(s => s.clave),
          ),
        },
        { hoy, estado, todas: series, cargos, inicioCuenta },
      );
      const visibles = consejosVisibles(lista, estado, hoy);
      if (visibles.length > 0) {
        consejos.set(id, visibles);
      }
    }
    return {
      series,
      encontrados,
      porCategoria,
      consejos,
      cargos,
      inicioCuenta,
      nombreCategoria,
    };
  }, [cargosLeidos, categorias, estado, fijos, hoy]);

  if (!activo) {
    return {
      activo,
      cargando: false,
      series: [],
      encontrados: [],
      porCategoria: new Map(),
      consejos: new Map(),
      estado,
      cambiar,
      cargos: [],
      inicioCuenta: new Map(),
      nombreCategoria: calculado.nombreCategoria,
    };
  }
  return {
    activo,
    cargando: cargosLeidos == null,
    ...calculado,
    estado,
    cambiar,
  };
}
